import {useCallback, useState} from 'react';

import {Dialog} from 'shells/dialog/dialog';
import {Modal} from 'shells/modal/modal';
import {PersonalEmailSequenceRow} from 'shells/row/personal-email-sequence-row/personal-email-sequence-row';
import type {PersonalEmailSequenceToView} from 'shells/row/personal-email-sequence-row/types';

import {QueuedEmailPreviewModal} from '../../../peep/email-marketing/email-sequences/queued-email-preview-modal';

import type {ConfirmState} from './types';

interface Props {
    personalEmailSequence: PersonalEmailSequenceToView;
    userId: string | null | undefined;
    onCancelSequence: (sequenceId: string) => void;
    onStartSequence: (sequenceId: string) => void;
    onSendEmailNow: (emailId: string) => void;
    onClose: () => void;
}

export function PersonalSequenceDetailsModal(props: Props) {
    const {onCancelSequence, onStartSequence, onSendEmailNow, onClose} = props;

    const [emailIdToPreview, setEmailIdToPreview] = useState<string | null | undefined>(null);
    const [cancelState, setCancelState] = useState<ConfirmState | null | undefined>(null);
    const [startState, setStartState] = useState<ConfirmState | null | undefined>(null);

    const handleConfirmCancel = useCallback(() => {
        if (!cancelState || !cancelState.sequenceId) {
            return;
        }

        onCancelSequence(cancelState.sequenceId);
        onClose();
    }, [onCancelSequence, cancelState, onClose]);

    const handleConfirmStart = useCallback(() => {
        if (!startState || !startState.sequenceId) {
            return;
        }

        onStartSequence(startState.sequenceId);
        setStartState(null);
    }, [onStartSequence, startState]);

    const handleSendEmailNow = useCallback(
        (emailId: string) => {
            onSendEmailNow(emailId);
        },
        [onSendEmailNow]
    );

    return (
        <>
            {!emailIdToPreview && (
                <Modal
                    isOpen={true}
                    onClose={props.onClose}
                    headerText='Personal email sequence details'
                >
                    <PersonalEmailSequenceRow
                        key={props.personalEmailSequence.id}
                        personalEmailSequence={props.personalEmailSequence}
                        htmlUrl={`/sales/email/templates`}
                        userId={props.userId}
                        onCancelButtonClick={(
                            sequenceId: string,
                            buttonRef?: HTMLElement | null
                        ) => {
                            setCancelState({
                                buttonRef,
                                sequenceId,
                            });
                        }}
                        onViewMessage={(emailId) => {
                            setEmailIdToPreview(emailId);
                        }}
                        onStartButtonClick={(
                            sequenceId: string,
                            buttonRef?: HTMLElement | null
                        ) => {
                            setStartState({
                                buttonRef,
                                sequenceId,
                            });
                        }}
                        onSendEmailNow={(emailId: string) => handleSendEmailNow(emailId)}
                        forceShowToolbar={Boolean(
                            (cancelState && cancelState.buttonRef) ||
                                (startState && startState.buttonRef)
                        )}
                    />
                </Modal>
            )}
            {emailIdToPreview && (
                <QueuedEmailPreviewModal
                    emailId={emailIdToPreview}
                    onClose={() => setEmailIdToPreview(null)}
                />
            )}
            {cancelState && cancelState.buttonRef && (
                <Dialog
                    anchor={cancelState.buttonRef}
                    question='Are you sure you want to cancel this email sequence?'
                    onPrimary={handleConfirmCancel}
                    onSecondary={() => {
                        setCancelState(null);
                    }}
                    primaryText='Yes, cancel'
                    secondaryText="No, don't cancel"
                    primaryVariant='danger'
                    secondaryVariant='secondary'
                    customStyles={{width: '240px'}}
                />
            )}
            {startState && startState.buttonRef && (
                <Dialog
                    anchor={startState.buttonRef}
                    question='Are you sure you want to start this email sequence?'
                    onPrimary={handleConfirmStart}
                    onSecondary={() => {
                        setStartState(null);
                    }}
                    primaryText='Yes, start'
                    secondaryText="No, don't start"
                    primaryVariant='primary'
                    secondaryVariant='secondary'
                    customStyles={{width: '220px'}}
                />
            )}
        </>
    );
}
